import { fillDate, fillDateForObjKeys } from '@/components/patient-stats/common/utils'
import { groupByDay } from '@/components/patient-stats/graphs/summary-reports/chart-options/rescue-inhaler-charts/buildRescueInhalerSeries'
import { DataPoint } from '@/jsonapi/CommonTypes'
import {
  buildAxis,
  buildMarkLine,
  getMarkLineRange,
} from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { customDotBarRenderer } from '@/components/patient-stats/graphs/custom-renderers'
import { removeAbbr } from '@/utils/time'
import { withSingularOrPluralLabel } from '@/utils/labels'
import YAxis = echarts.EChartOption.YAxis
import XAxis = echarts.EChartOption.XAxis

export function buildOverUsageOptions(options: {
  resultStartTimestamp: number
  resultEndTimestamp: number
  timezone: string
  colors: Record<any, any> // vuetify theme
  overUsages: DataPoint[]
}): echarts.EChartOption<echarts.EChartOption.SeriesCustom> {
  const { timezone, resultStartTimestamp, resultEndTimestamp, colors, overUsages } = options

  const xAxisLabels: string[] = [...fillDate(timezone, resultStartTimestamp, resultEndTimestamp)].map(timestamp =>
    String(timestamp)
  )

  const flattenOverUsagesDataPoint = overUsages
    .map(dataPoint => {
      const newDataPoint = {
        ...dataPoint,
        value: 1,
      }
      return Array(dataPoint.value).fill(newDataPoint)
    })
    .flat(1)
  const overUsagesByDay = groupByDay<DataPoint>(flattenOverUsagesDataPoint, timezone)
  const filledOverUsagesByDay: { [key: string]: DataPoint[] | null } = fillDateForObjKeys<DataPoint[] | null>(
    xAxisLabels,
    overUsagesByDay,
    null
  )
  const seriesData = Object.entries(filledOverUsagesByDay)
    .map(([key, values]) => {
      return Array.isArray(values) ? values.map((value: DataPoint, index) => [key, index, index + 1]) : [[key, 0, 0]]
    })
    .flat(1)

  const maxYValue = Math.max(
    4,
    Object.values(overUsagesByDay).reduce((acc, cur) => Math.max(cur.length, acc), 0)
  )

  const lines = getMarkLineRange({
    maxYValue: maxYValue,
    minStep: 1,
    minEnd: 4,
    numOfSeparate: 4,
  })

  return {
    grid: [{ x: '2.5%', y: '5%', width: '95%', height: '95%', containLabel: true } as echarts.EChartOption.Grid],
    xAxis: buildAxis({
      data: xAxisLabels,
      axisType: 'x',
      formatter: param => {
        return moment(parseInt(param, 10))
          .tz(timezone)
          .format(CHART_DATE_FORMAT.DATE)
      },
    }) as XAxis,
    yAxis: buildAxis({
      axisType: 'y',
      maxValue: maxYValue,
    }) as YAxis,
    series: [
      {
        type: 'line',
        silent: true,
        // @ts-ignore
        markLine: buildMarkLine(lines),
      },
      {
        type: 'custom',
        silent: true,
        itemStyle: {
          color: colors.burgundy,
        },
        // @ts-ignore
        renderItem: customDotBarRenderer,
        data: seriesData,
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: params => {
        if (!Array.isArray(params) || params.reduce((acc, cur) => acc + cur.value[2], 0) === 0) {
          return null
        }
        const dateLabel = moment(parseInt(params[0].axisValue as string, 10))
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.DAY_ABBR))
        return `${dateLabel}<br>${withSingularOrPluralLabel(params.length, 'time', 'times')}`
      },
    },
  }
}
